import React, { useRef, useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import {
    Paper,
    InputBase,
    IconButton,
    Box,
    Divider,
    Fade
} from '@mui/material';
import { styled } from '@mui/material/styles';
import GoogleIcon from '@mui/icons-material/Google';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import PlaceIcon from '@mui/icons-material/Place';
import {
    setWorkingLatLng,
    setPlaceRoutePointOnClick
} from '../../../features/tripcenter/googleRouteMakerSlice';

// Styled search container
const SearchPaper = styled(Paper)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    maxWidth: '800px',
    borderRadius: theme.shape.borderRadius,
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    transition: 'box-shadow 0.3s ease',
    '&:hover': {
        boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
    },
    border: '1px solid',
    borderColor: theme.palette.divider,
    backgroundColor: theme.palette.background.paper,
    overflow: 'hidden'
}));

// Enhanced search input component
const GoogleMapSearch = () => {
    const dispatch = useDispatch();
    const inputRef = useRef(null);
    const [inputValue, setInputValue] = useState('');
    const [focused, setFocused] = useState(false);

    // Handle input change
    const handleInputChange = (e) => {
        setInputValue(e.target.value);
    };

    // Clear the input and reset the working location
    const handleClear = () => {
        setInputValue('');
        if (inputRef.current) {
            inputRef.current.value = '';
            inputRef.current.focus();
        }
        // Reset the working location in Redux
        dispatch(setWorkingLatLng(null));

        // Re-enable the place route point on click functionality
        dispatch(setPlaceRoutePointOnClick(true));
    };

    // Initialize the Google Places Autocomplete
    useEffect(() => {
        if (inputRef.current && window.google && window.google.maps) {
            const autocomplete = new window.google.maps.places.Autocomplete(inputRef.current);

            autocomplete.addListener('place_changed', () => {
                const place = autocomplete.getPlace();
                if (place.geometry && place.geometry.location) {
                    // Update input value for controlled component
                    setInputValue(place.formatted_address || place.name);

                    // Update the working location in Redux
                    dispatch(setWorkingLatLng({
                        lat: place.geometry.location.lat(),
                        lng: place.geometry.location.lng()
                    }));
                }
            });

            // Prevent form submission on Enter
            inputRef.current.addEventListener('keydown', (e) => {
                if (e.key === 'Enter') e.preventDefault();
            });
        }
    }, [dispatch]);

    return (
        <SearchPaper
            elevation={0}
            sx={{
                p: 0.5,
                m: 1,
                bgcolor: focused ? 'background.paper' : 'grey.50'
            }}
        >
            <IconButton
                sx={{
                    p: 1.5,
                    color: 'primary.main'
                }}
                aria-label="search"
            >
                <GoogleIcon />
            </IconButton>

            <InputBase
                inputRef={inputRef}
                fullWidth
                placeholder="Google Maps Search Location"
                value={inputValue}
                onChange={handleInputChange}
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
                sx={{
                    ml: 1,
                    flex: 1,
                    '& .MuiInputBase-input': {
                        py: 1.25,
                        fontSize: '0.95rem',
                        fontWeight: 400,
                        color: 'text.primary',
                        '&::placeholder': {
                            opacity: 0.7,
                            color: 'text.secondary',
                        }
                    }
                }}
                endAdornment={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {inputValue && (
                            <Fade in={Boolean(inputValue)}>
                                <IconButton
                                    size="small"
                                    onClick={handleClear}
                                    aria-label="clear"
                                    sx={{
                                        mr: 0.5,
                                        color: 'text.secondary',
                                        '&:hover': {
                                            color: 'error.main',
                                            bgcolor: 'error.lightest'
                                        }
                                    }}
                                >
                                    <ClearIcon fontSize="small" />
                                </IconButton>
                            </Fade>
                        )}
                        <Divider orientation="vertical" flexItem sx={{ height: 24, mx: 0.5 }} />
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                px: 1.5,
                                py: 0.75,
                                mr: 0.5,
                                borderRadius: 1,
                                color: 'primary.main',
                                bgcolor: 'primary.50',
                                fontSize: '0.75rem',
                                fontWeight: 500
                            }}
                        >
                            <PlaceIcon fontSize="small" sx={{ mr: 0.5 }} />
                            Map
                        </Box>
                    </Box>
                }
            />
        </SearchPaper>
    );
};

export default GoogleMapSearch;